/**
 * Where output goes (spec §2 F) and who reviews it (§2 G).
 *
 * Deliveries are queued (enqueue_agent_delivery) and sent by the ambient
 * agent_run job through the workspace's channel policy — in-app, Slack, email
 * digest and the workspace's daily cap — then this agent's own floor, digest
 * and daily cap. Reviewers are enforced in the database: accepting or
 * rejecting this agent's output needs one of these roles, or administrator.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Select } from '../../ds';
import { DEFAULT_JOB_ROLES } from '../../../types/prioritization/jobRoles';
import { NOTIFY_AUDIENCES, type AgentDeliveryDefaults } from '../../../lib/ai/agentDefinitions';
import { getAmbientChannelPolicy, type AmbientChannelPolicy } from '../../../lib/ambientRuntimeService';
import { MIN_REVIEWS_FOR_CALIBRATION } from '../../../lib/agentCalibrationService';
import { ChipPicker, SwitchGroup, SwitchRow, type SectionProps } from './shared';
import { stored } from './resolve';
import { acceptanceWords, VALUE_AI_SETTINGS_HREF } from '../behaviors/acceptanceWords';

const ROLE_OPTIONS = DEFAULT_JOB_ROLES.filter((r) => r.slug !== 'workspace-member').map((r) => ({ key: r.slug, label: r.displayName }));

const VERB_WORDS: Record<string, string> = {
  accept: 'accept',
  accept_with_change: 'accept with a change',
  partial_accept: 'accept in part',
  reject: 'reject with a reason',
  dismiss: 'dismiss',
  reopen: 'reopen',
};

export function DeliverySection({ def, config, workspaceId, isSaving, canEdit, save, surfaceOnObjectHelp }: SectionProps & { surfaceOnObjectHelp: string }) {
  const d = stored<AgentDeliveryDefaults>(def.delivery, config?.delivery);
  const put = (patch: Partial<AgentDeliveryDefaults>, ok?: string) => save({ delivery: { ...d, ...patch } }, ok);
  const [policy, setPolicy] = useState<AmbientChannelPolicy | null>(null);
  const [cap, setCap] = useState(String(d.dailyCap));
  useEffect(() => setCap(String(d.dailyCap)), [d.dailyCap]);
  useEffect(() => { void getAmbientChannelPolicy(workspaceId).then((r) => setPolicy(r.data)); }, [workspaceId]);

  const toggle = (key: string) =>
    void put({ notifyRoles: d.notifyRoles.includes(key) ? d.notifyRoles.filter((k) => k !== key) : [...d.notifyRoles, key] });

  const channels = policy
    ? [policy.inAppEnabled && 'in the app', policy.slackEnabled && 'in Slack', policy.emailDigestEnabled && 'in the email digest'].filter(Boolean).join(', ') || 'nowhere (every channel is off)'
    : '…';

  return (
    <SwitchGroup title="Where its output goes" lead="What it produces appears where that kind of thing already shows. These switches decide who is told, and how often.">
      <SwitchRow label="Show it on the item" help={surfaceOnObjectHelp}
        on={d.surfaceOnObject} disabled={isSaving || !canEdit} onChange={() => void put({ surfaceOnObject: !d.surfaceOnObject })} />

      <div className="border-t border-[var(--rule)] pt-3 space-y-2">
        <p className="text-sm font-medium text-[var(--text-pri)]">Who is told</p>
        <ChipPicker options={NOTIFY_AUDIENCES} chosen={d.notifyRoles} disabled={isSaving || !canEdit} onToggle={toggle} />
        <details>
          <summary className="text-xs text-[var(--text-sec)] cursor-pointer">People holding a role</summary>
          <div className="pt-2"><ChipPicker options={ROLE_OPTIONS} chosen={d.notifyRoles} disabled={isSaving || !canEdit} onToggle={toggle} /></div>
        </details>
        <p className="text-[11px] text-[var(--text-ter)]">
          Sent {channels}, by this workspace's channel policy (<Link to="/prioritization/settings/ambient-runtime" className="underline">change it</Link>), up to {policy?.dailyDeliveryCap ?? 50} messages a day across the workspace.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3 border-t border-[var(--rule)] pt-3">
        {def.delivery.confidenceFloor !== undefined && (
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Announce proposals at least {Math.round((d.confidenceFloor ?? 0) * 100)}% sure</label>
            <input type="range" min={0} max={90} step={5} value={Math.round((d.confidenceFloor ?? 0) * 100)} disabled={isSaving || !canEdit}
              onChange={(e) => void put({ confidenceFloor: Number(e.target.value) / 100 })} className="w-full accent-[var(--slate)]" aria-label="Announcement floor" />
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1" htmlFor={`digest-${def.agentType}`}>How often</label>
          <Select id={`digest-${def.agentType}`} value={d.digest} disabled={isSaving || !canEdit} onChange={(e) => void put({ digest: e.target.value as AgentDeliveryDefaults['digest'] })}>
            <option value="per_run">After each run</option>
            <option value="daily">Once a day, gathered</option>
            <option value="weekly">Once a week, gathered</option>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1" htmlFor={`cap-${def.agentType}`}>At most, per day</label>
          <input id={`cap-${def.agentType}`} type="number" min={1} max={500} value={cap} readOnly={!canEdit}
            onChange={(e) => setCap(e.target.value)}
            onBlur={() => { const n = Math.max(1, Math.min(500, Math.round(Number(cap) || 1))); if (n !== d.dailyCap) void put({ dailyCap: n }); }}
            className="w-full px-2 py-1 text-sm border border-[var(--rule)] bg-[var(--surface)]" />
        </div>
      </div>

      {def.outputs.includes('finding') && (
        <div className="border-t border-[var(--rule)] pt-3 max-w-xs">
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1" htmlFor={`severity-${def.agentType}`}>Announce findings at least this severe</label>
          <Select id={`severity-${def.agentType}`} value={d.severityFloor ?? 'medium'} disabled={isSaving || !canEdit}
            onChange={(e) => void put({ severityFloor: e.target.value as NonNullable<AgentDeliveryDefaults['severityFloor']> })}>
            {(['info', 'low', 'medium', 'high', 'critical'] as const).map((v) => <option key={v} value={v}>{v[0].toUpperCase() + v.slice(1)}</option>)}
          </Select>
        </div>
      )}

      {def.outputs.includes('task') && (
        <SwitchRow label="Ask the item's owner when it cannot do its job" help="A task for the owner to say more, instead of a silent abstain. One open ask per item."
          on={d.createTasks} disabled={isSaving || !canEdit} onChange={() => void put({ createTasks: !d.createTasks })} />
      )}
    </SwitchGroup>
  );
}

export function ReviewSection({ def, config, isSaving, canEdit, save }: SectionProps) {
  const roles = config?.reviewRoles?.length ? config.reviewRoles : def.reviewRoles;
  const toggle = (key: string) => {
    const next = roles.includes(key) ? roles.filter((r) => r !== key) : [...roles, key];
    if (!next.length) return;
    void save({ reviewRoles: next }, 'Reviewers saved');
  };
  return (
    <SwitchGroup title="Who reviews it" lead={`Nothing it produces takes effect until a person reviews it (${def.reviewVerbs.map((v) => VERB_WORDS[v]).join(', ')}). Workspace administrators always may; otherwise the person needs one of these roles. The database enforces it.`}>
      <ChipPicker options={ROLE_OPTIONS} chosen={roles} disabled={isSaving || !canEdit} onToggle={toggle} />
      <p className="text-[11px] text-[var(--text-ter)]">
        {acceptanceWords(def)}
        {def.outputs.includes('proposal:value') && (
          <> <Link to={VALUE_AI_SETTINGS_HREF} className="underline">Each value's AI setting</Link></>
        )}
      </p>
      {def.bulkAcceptAllowed && (
        <>
          <SwitchRow label="Accept many at once above a calibrated confidence"
            help={`Owner ruling: allowed when an administrator turns it on. Only confidence buckets with ${MIN_REVIEWS_FOR_CALIBRATION} or more reviews count; each acceptance is logged on its own and can be reversed alone.`}
            on={config?.bulkAcceptEnabled ?? false} disabled={isSaving || !canEdit}
            onChange={() => void save({ bulkAcceptEnabled: !(config?.bulkAcceptEnabled ?? false) })} />
          {config?.bulkAcceptEnabled && (
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Floor: {Math.round((config.bulkAcceptFloor ?? 0.8) * 100)}% calibrated confidence</label>
              <input type="range" min={50} max={95} step={5} value={Math.round((config.bulkAcceptFloor ?? 0.8) * 100)} disabled={isSaving || !canEdit}
                onChange={(e) => void save({ bulkAcceptFloor: Number(e.target.value) / 100 })} className="w-full max-w-sm accent-[var(--slate)]" aria-label="Bulk accept floor" />
            </div>
          )}
        </>
      )}
    </SwitchGroup>
  );
}
